import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { Category } from "../types/domain";
import { categoryPath, childrenOf } from "../lib/categories";

type Props = {
  categories: Category[];
  value: number | null;
  onChange: (categoryId: number) => void;
  compact?: boolean;
  getIcon?: (category: Category, columnIndex: number) => ReactNode;
  commitParents?: boolean;
};

export function CategoryTreePicker({ categories, value, onChange, compact = false, getIcon, commitParents = false }: Props) {
  const committedPath = categoryPath(categories, value);
  const [expandedPath, setExpandedPath] = useState<Category[]>([]);
  const treeRef = useRef<HTMLDivElement | null>(null);
  const closeAfterSelectRef = useRef(false);
  const [collapsedMobileCategoryIds, setCollapsedMobileCategoryIds] = useState<Set<number>>(() => new Set());
  const path = compact ? expandedPath : committedPath;
  const committedIds = new Set(committedPath.map((category) => category.id));
  const rootSelectedId = compact ? expandedPath[0]?.id ?? committedPath[0]?.id ?? null : path[0]?.id ?? null;
  const columns: Array<{ parentId: number | null; selectedId: number | null }> = [{ parentId: null, selectedId: rootSelectedId }];

  useEffect(() => {
    if (!compact) return;
    if (closeAfterSelectRef.current) {
      closeAfterSelectRef.current = false;
      return;
    }
    setExpandedPath(committedPath);
  }, [compact, value, categories]);

  useEffect(() => {
    if (!compact) return;
    const close = (event: MouseEvent) => {
      if (!treeRef.current?.contains(event.target as Node)) setExpandedPath(committedPath);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [compact, value, categories]);

  path.forEach((category, index) => {
    if (childrenOf(categories, category.id).length) {
      columns.push({ parentId: category.id, selectedId: path[index + 1]?.id ?? null });
    }
  });

  const chooseCategory = (category: Category, columnIndex: number) => {
    const children = childrenOf(categories, category.id);
    if (!compact || !children.length || commitParents) {
      onChange(category.id);
    }
    if (compact) {
      if (children.length) setExpandedPath((current) => [...current.slice(0, columnIndex), category]);
      else {
        closeAfterSelectRef.current = true;
        setExpandedPath([]);
      }
    }
  };

  const chooseMobileCategory = (category: Category, columnIndex: number) => {
    const children = childrenOf(categories, category.id);
    if (children.length && committedIds.has(category.id)) {
      setCollapsedMobileCategoryIds((current) => {
        const next = new Set(current);
        if (next.has(category.id)) next.delete(category.id);
        else next.add(category.id);
        return next;
      });
      onChange(category.id);
      return;
    }
    setCollapsedMobileCategoryIds(new Set());
    chooseCategory(category, columnIndex);
  };

  const renderMobileLevel = (parentId: number | null, depth = 0): ReactNode => {
    const parentCategory = parentId ? categories.find((category) => category.id === parentId) : null;
    return (
      <div className="category-mobile-options" data-depth={depth}>
        {parentCategory ? (
          <button
            className="category-choice category-choice-all category-mobile-choice-all"
            type="button"
            aria-pressed={value === parentCategory.id}
            onClick={() => {
              setCollapsedMobileCategoryIds(new Set());
              onChange(parentCategory.id);
            }}
          >
            <span className="category-choice-label">Все в {parentCategory.name}</span>
          </button>
        ) : null}
        {childrenOf(categories, parentId).map((category) => {
          const children = childrenOf(categories, category.id);
          const hasChildren = children.length > 0;
          const expanded = hasChildren && committedIds.has(category.id) && !collapsedMobileCategoryIds.has(category.id);
          return (
            <div className="category-mobile-branch" key={category.id}>
              <button
                className="category-choice category-mobile-choice"
                type="button"
                aria-pressed={value === category.id}
                aria-expanded={hasChildren ? expanded : undefined}
                onClick={() => chooseMobileCategory(category, depth)}
              >
                {getIcon ? getIcon(category, depth) : null}
                <span className="category-choice-label">{category.name}</span>
                {hasChildren ? <span className="category-mobile-arrow" aria-hidden="true">›</span> : null}
              </button>
              {expanded ? renderMobileLevel(category.id, depth + 1) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`category-tree${compact ? " category-tree-compact" : ""}`} ref={treeRef}>
      {columns.map((column, columnIndex) => {
        const parentCategory = column.parentId ? categories.find((category) => category.id === column.parentId) : null;
        return (
          <div className="category-column" style={compact ? { "--category-column-offset": `${Math.max(0, columnIndex - 1) * 274}px` } as CSSProperties : undefined} key={column.parentId ?? "root"}>
            {compact && parentCategory ? (
              <button
                className="category-choice category-choice-all"
                type="button"
                aria-pressed={value === parentCategory.id}
                onClick={() => {
                  closeAfterSelectRef.current = true;
                  onChange(parentCategory.id);
                  setExpandedPath([]);
                }}
              >
                <span className="category-choice-label">Все в {parentCategory.name}</span>
              </button>
            ) : null}
            {childrenOf(categories, column.parentId).map((category) => (
              <button
                className="category-choice"
                type="button"
                aria-pressed={column.selectedId === category.id}
                key={category.id}
                onClick={() => chooseCategory(category, columnIndex)}
              >
                {getIcon ? getIcon(category, columnIndex) : null}
                <span className="category-choice-label">{category.name}</span>
                {childrenOf(categories, category.id).length ? <span aria-hidden="true">›</span> : null}
              </button>
            ))}
          </div>
        );
      })}
      {!compact ? <div className="category-tree-mobile-cascade">{renderMobileLevel(null)}</div> : null}
    </div>
  );
}
